import React, {  useState, useEffect, useContext } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { 
	Row,
	Col,
	message, 
	Select,
	Form,
	Button, 
	Card,
	Divider,
	Modal,
	Typography,
	InputNumber,
	theme,
	Checkbox,
} from "antd";
import PropTypes from "prop-types";
import { 
	dte,
	getDefaultFrases,
	getSelectDefaultFrases,
	getOptionalFrases,
	ivaRegimenExento,
	taxesByDte,
	satFrases,
	satTipos,
	dteNoIVA
} from "../../../services";
import { 
	EmailConfig,
	InvoiceLine,
	calculateTaxValue,
	SelectClient,
} from "./components";
import { calculateInvoiceTotal, formatNumber } from "../../../utils/CalculateInvoiceTotal";
import { formatStructureForNotification } from "./restructureStructures";
import { PdfReportReview } from "../../reports";
import { InvoiceEmitTemplate } from "../invoice_templates";
import { GeneralAppDataContext } from "../../../contexts";
import { WaitingForACompany } from "../../WaitingForACompany";
import { Tooltip } from "antd";

const { Title } = Typography;

export function formatCurrency(value) {
	if (typeof value !== "number") {
		return "";
	}
	
	return value.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

export const FillInvoiceData = ({
	token, 
	company, 
	invoice, 
	setInvoice,
	clients, 
	recurrenceRules = {}, 
	emailData = null, 
	onSaveChanges = null,
	loading = false,
	isCreate = false
}) => {
	const { token: antToken } = theme.useToken();
	const { generalAppData } = useContext(GeneralAppDataContext);

	const [ changes, setChanges ] = useState(0);
	const [ frases, setFrases ] = useState([]);
	const [ originalFrases, setOriginalFrases ] = useState([]);
	const [ obligatorySelectFrases, setObligatorySelectFrases ] = useState([]);
	const [ optionalSelectFrases, setOptionalSelectFrases ] = useState([]);
	const [ openFrasesModal, setOpenFrasesModal ] = useState(false);
	const [ openReviewInvoice, setOpenReviewInvoice ] = useState(false);
	const [ openEmailsModal, setOpenEmailsModal ] = useState(false);
	const [ isRecurrency, setIsRecurrency ] = useState(false);
	const [ clientExchangeRate, setClientExchangeRate ] = useState(generalAppData.exchangeRates.gtq_usd.general);
	const [ form ] = Form.useForm();

	const handleOpenFrasesModal = () => {
		setOpenFrasesModal(true);
	};

	const handleCloseFrasesModal = () => {
		setOpenFrasesModal(false);
	};

	const handleSelectEstablishment = (value) => {
		handleFieldChange("establishment", "establishment", value);
		setChanges(changes + 1);
	};

	const handleSelectDteType = (value) => {
		handleFieldChange("type", "type", value);
		setChanges(changes + 1);
	};

	const handleSelectCurrency = (value) => {
		handleFieldChange("currency", "currency", value);
		setChanges(changes + 1);
	};

	const handleChangeClientExchangeRate = (value) => {
		setClientExchangeRate(value);
		handleFieldChange("xchange_rate", "xchange_rate", value);
		setChanges(changes + 1);
	};

	const groupByType = (data) => {
		return data.reduce((acc, item) => {
			if (!acc[item.type]) {
				acc[item.type] = [];
			}
			acc[item.type].push(item.scenario);
			return acc;
		}, {});
	};

	const handleSelectChange = (type, scenario) => {
		setFrases(prev => {
			const scenarioExists = prev.some(
				item => item.type === type
			);
    
			if (scenarioExists) {
				return [...prev.filter(item => item.type !== type), { type, scenario }];
			} else {
				return [...prev, { type, scenario }];
			}
		});
	};

	const renderSelects = (groupedData, label) => {
		if (Object.keys(groupedData)?.length === 0) {
			return <></>;
		}

		return (
			<div>
				<h2>{label}</h2>
				{Object.keys(groupedData).map(type => (
					<div key={type}>
						<h3>{satTipos[type]}</h3>
						<Select
							style={{width:"100%"}}
							onChange={(e) => handleSelectChange(type, e)}
							isMulti
							options={groupedData[type].map(scenario => ({ label: satFrases[type][scenario], value: scenario }))}
						/>
					</div>
				))}
			</div>
		);
	};

	const checkMandatoryTypes = (mandatoryData, selectedScenarios) => {
		const selectedTypes = new Set(selectedScenarios.map(item => item.type));
        
		for (let type in mandatoryData) {
			if (!selectedTypes.has(type)) {
				return false;
			}
		}
        
		return true;
	};

	const onSaveInvoice = () => {
		// Validar las reglas de recurrencia
		if(recurrenceRules?.frequency === null || 
			recurrenceRules?.interval === 0 || 
			recurrenceRules?.start_date === null || 
			recurrenceRules?.start_date === ""){
			message.error("Por favor ingrese todos los datos de recurrencia");
			return;
		}
	
		// Validar que los ítems no estén vacíos
		if (form.getFieldValue("items") && form.getFieldValue("items")?.length < 1) {
			message.error("Por favor ingrese al menos un ítem");
			return;
		}
	
		// Validar los campos del formulario
		form.validateFields()
			.then((values) => {
				// Validar frases obligatorias
				if (!isMandatoryTypesSelected) {
					message.error("No ha seleccionado las frases obligatorias");
				} else {
					// Si todo es correcto, abrir el modal y setear los valores
					values.frases = frases;
					setOpenReviewInvoice(true);					
					setInvoice(values);  // Setear los valores en el estado
				}
			})
			.catch(errorInfo => {
				console.error("Validation failed:", errorInfo);
				setOpenEmailsModal(false);
				setOpenReviewInvoice(false);
				message.error("Debe rellenar toda la información");
			});
	
		// Verificar si se seleccionaron las frases obligatorias
		const isMandatoryTypesSelected = checkMandatoryTypes(obligatorySelectFrases, frases);
		setChanges(changes + 1);
	};
	
	const activeEmailModal = () => {		
		setOpenReviewInvoice(false);
		setOpenEmailsModal(true);
	};

	function inicialesEnMayusculas(texto) {
		// Dividir el texto en palabras usando espacios como separadores
		const palabras = texto.split(" ");
    
		// Mapear cada palabra para obtener la primera letra en mayúscula
		const iniciales = palabras.map(palabra => {
			if (palabra?.length > 0) {
				return palabra[0].toUpperCase();
			}
			return ""; // En caso de que haya palabras vacías, retornamos una cadena vacía
		});
    
		// Unir las iniciales en una sola palabra
		const resultado = iniciales.join("");
    
		return resultado;
	}

	const calculateTotalDescription = (quantity, price, discount = 0, taxes = [], currency, company) => {
		// Asegurar que quantity, price, discount son números válidos, de lo contrario, asignar 0
		quantity = isNaN(quantity) ? 0 : quantity;
		price = isNaN(price) ? 0 : price;
		discount = isNaN(discount) ? 0 : discount;
    
		const base = quantity * price;
		const baseWithDiscount = base - discount;
		const dteType = form.getFieldValue("type")? form.getFieldValue("type") : "";
        
		// Asegurar que baseWithDiscount es válido y no NaN
		const baseWithDiscountSafe = isNaN(baseWithDiscount) ? 0 : baseWithDiscount;
    
		let baseNoIVA = baseWithDiscountSafe / 1.12;
		let iva = baseNoIVA * 0.12;

		// Validar baseNoIVA e iva
		baseNoIVA = isNaN(baseNoIVA) ? 0 : baseNoIVA;
		iva = isNaN(iva) ? 0 : iva;
    
		let additionalCosts = [];
    
		// Comprobar si el régimen de IVA es exento
		if (ivaRegimenExento.includes(company.tax_authority_specifications.iva_regimen) || dteNoIVA.includes(dteType)) {
			baseNoIVA = baseWithDiscountSafe;
			iva = 0;
		}

    
		taxes.forEach(tax => {
			const taxDetails = taxesByDte[dteType][tax];
			const value = calculateTaxValue(taxDetails, quantity, price, discount, currency, 7.8, company);
            
			// Validar el valor calculado del impuesto
			const safeValue = isNaN(value) ? 0 : value;
    
			additionalCosts.push([tax, safeValue]);
		});
    
		let calculusHTML = `<p style="margin: 0; text-align: right; color: ${antToken.colorPrimary};">${formatNumber(base)}</p>`;
    
		if (discount !== 0) {
			calculusHTML += `
                <p style="margin: 0; text-align: right; color: ${antToken.colorPrimary};">(DESC.) -${formatNumber(discount)}</p>
                <span style="display: block; border-top: 1px solid ${antToken.colorPrimary}; margin: 2px 0;"></span>
                <p style="margin: 0; text-align: right; color: ${antToken.colorPrimary};">(BASE) ${formatNumber(baseWithDiscountSafe)}</p>
            `;
		}
    
		if (iva !== 0) {
			calculusHTML += `
                <span style="display: block; border-top: 1px solid ${antToken.colorPrimary}; margin: 2px 0;"></span>
                <p style="margin: 0; text-align: right; color: ${antToken.colorPrimary};">${formatNumber(baseNoIVA)}</p>
                <p style="margin: 0; text-align: right; color: ${antToken.colorPrimary};">(IVA) +${formatNumber(iva)}</p>
            `;
		}
    
		let total = baseWithDiscountSafe;
		additionalCosts.forEach(tax => {
			if (tax[0] !== "IVA" && tax[0] !== "IVA EXENTO") {
				calculusHTML += `<p style="margin: 0; text-align: right; color: ${antToken.colorPrimary};">(${inicialesEnMayusculas(tax[0] ? tax[0] : "")}) +${formatNumber(tax[1])}</p>`;
				total += tax[1];
			}
		});
    
		// Asegurarse de que el total sea válido y no NaN
		total = isNaN(total) ? 0 : total;
    
		calculusHTML += `
            <span style="display: block; border-top: 1px solid ${antToken.colorPrimary}; margin: 2px 0;"></span>
            <p style="margin: 0; text-align: right; font-weight:bold; color: ${antToken.colorPrimary};">(TOTAL) ${formatNumber(total)}</p>
        `;
    
		let detailed = {
			base: isNaN(base) ? 0 : base,
			discount: isNaN(discount) ? 0 : discount,
			baseWithDiscount: baseWithDiscountSafe,
			iva,
			baseNoIVA,
			additionalCosts,
			total
		};
    
		return { total, calculusHTML, detailed };
	};

	// Función separada para actualizar y formatear los ítems con el total calculado
	const updateItemsWithTotal = (items) => {
		return items.map((item) => {
			const {total, calculusHTML, detailed} = calculateTotalDescription(
				item.quantity, 
				item.priceUnit, 
				item.discount, 
				item.taxes, 
				form.getFieldValue("currency"), 
				company
			);
			const formattedTotal = formatNumber(total);

			return {
				...item,
				total: formattedTotal,
				calculusHTML,
				detailed
			};
		});
	};
    
	// Función principal para manejar cambios en el formulario
	const handleFieldChange = (fieldIndex, key, value) => {
		const items = form.getFieldValue("items");
        
		if (typeof fieldIndex === "number") {
			// Caso 1: Actualizar un ítem específico
			items[fieldIndex][key] = value;
            
			// Recalcular y formatear el total para todos los ítems
			const updatedItems = updateItemsWithTotal(items);
            
			// Actualizar los valores del formulario con los ítems modificados
			form.setFieldsValue({ items: updatedItems });
            
		} else if (fieldIndex == "refresh") {
			// Recalcular y formatear el total para todos los ítems
			const updatedItems = updateItemsWithTotal(items);
            
			// Actualizar los valores del formulario con los ítems modificados
			form.setFieldsValue({ items: updatedItems });
		} else {
			// Caso 2: Actualizar un valor específico del formulario (no relacionado con los ítems)
			const formData = form.getFieldsValue();
            
			// Actualizar el valor específico en el formulario
			formData[key] = value;
            
			// Si el valor específico afecta al formateo de items, recalcular los totales de los ítems
			if (key === "currency" || key === "type") {
				const updatedItems = updateItemsWithTotal(items);
				formData["items"] = updatedItems; // Actualizar los ítems en el formulario

				if (key === "type") {
					setFrases(getDefaultFrases(company.tax_authority_specifications, value));
					setObligatorySelectFrases(groupByType(getSelectDefaultFrases(company.tax_authority_specifications, value)));
					setOptionalSelectFrases(groupByType(getOptionalFrases(company.tax_authority_specifications, value)));
				}
			}
            
			// Actualizar los valores del formulario con los datos modificados
			form.setFieldsValue(formData);
		}
        
		// Opción adicional: Si necesitas seguir rastreando cambios para otros fines
		setChanges(changes + 1);
	};
	
	const transformToInvoice = (data) => {				
		return {
			client: {
				address: data?.receptor?.address || "",
				id: data?.receptor?.id || "",
				name: data?.receptor?.name || "",
				tid: data?.receptor?.tid || "",
				type: data?.receptor?.type || "",
			},
			xchange_rate: data?.specs?.xchange_rate || "",
			frases: data?.frases,
			type: data?.type,
			currency: data?.currency,
			establishment: data?.establishment_id,
			items: data.items.map(item => ({
				quantity: item.quantity,
				description: item.description,
				priceUnit: item.priceUnit,
				discount: item.discount || "0",
				taxes: item.taxes.map(tax => tax.shortName),
				type: item.type,
				accounting_account: item.accounting_account || "",
			})),
			email: data.email
		};
	};

	useEffect(() => {
		if (invoice && !invoice.create && Object.keys(invoice)?.length !== 0) {
			const invoiceToSet = transformToInvoice(invoice);
			form.setFieldsValue(invoiceToSet);
			setFrases(getDefaultFrases(company.tax_authority_specifications, invoiceToSet.type));
			setObligatorySelectFrases(groupByType(getSelectDefaultFrases(company.tax_authority_specifications, invoiceToSet.type)));
			setOptionalSelectFrases(groupByType(getOptionalFrases(company.tax_authority_specifications, invoiceToSet.type)));
			setOriginalFrases(invoiceToSet.frases);
			// Recalcular y formatear el total para todos los ítems
			const updatedItems = updateItemsWithTotal(invoiceToSet.items);

			// Actualizar los valores del formulario con los ítems modificados
			form.setFieldsValue({ items: updatedItems });
			setClientExchangeRate(invoiceToSet.xchange_rate);
			setChanges(changes+1);
			setIsRecurrency(true);
		}
	}, []);
		
	return (
		<>
			{
				company.id !== "" &&
				<>
					<Form
						title=""
						form={form}
						name="sendInvoice"
						initialValues={{ items: [{}] }}
						layout="horizontal"
						//variant="filled"
					>
						<Row gutter={[10, 0]}>
							<Col span={12} >
								<Row gutter={[10, 0]}>
									<Col span={24} >
										<Form.Item
											name='type'
											rules={[{ required: true, message: "Por favor ingrese un Tipo de DTE" }]}
										>
											<Title level={5} style={{ margin: "0", padding: "0" }}>Tipo de DTE</Title>
											<Select
												showSearch
												placeholder="Selecciona un tipo de DTE"
												optionFilterProp="label"
												onChange={handleSelectDteType}
												filterOption={(input, option) =>
													(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
												}
												value={form.getFieldValue("type")}
												options={ 
													company.tax_authority_specifications.iva_regimen?
														dte[company.tax_authority_specifications.iva_regimen] : 
														[]
												}
												//disabled={isRecurrency}
											/>
										</Form.Item>
									</Col>
									<Col span={12} > 
										<Form.Item
											name='establishment'
											rules={[{ required: true, message: "Por favor ingrese un Establecimiento" }]}
										>
											<Title level={5} style={{ margin: "0", padding: "0" }}>Establecimiento</Title>
											<Select
												showSearch
												style={{ width: "100%" }}
												placeholder="Selecciona un Establecimiento"
												optionFilterProp="label"
												onChange={handleSelectEstablishment}
												value={form.getFieldValue("establishment")}
												filterOption={(input, option) =>
													(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
												}
												options = {
													company.tax_authority_specifications.establishments
														? company.tax_authority_specifications.establishments
															.filter(e => e.state)
															.map(e => ({
																value: e.id,
																label: e.name
															}))
														: []
												}
												//disabled={isRecurrency}
											/>
										</Form.Item>
									</Col>
									<Col span={12} >
										<Form.Item
											name='currency'
											rules={[{ required: true, message: "Por favor ingrese una Moneda" }]}
										>
											<Title level={5} style={{ margin: "0", padding: "0" }}>Moneda</Title>
											<Select
												showSearch
												style={{ width: "100%" }}
												placeholder="Selecciona una Moneda"
												optionFilterProp="label"
												onChange={handleSelectCurrency}
												filterOption={(input, option) =>
													(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
												}
												value={form.getFieldValue("currency")}
												options={
													[
														{
															value: "GTQ",
															label: "GTQ - Quetzales"
														},
														{
															value: "USD",
															label: "USD - Dólares"
														}
													]
												}
												//disabled={isRecurrency}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Col>
							<Col span={12}>
								<SelectClient
									company={company}
									handleFieldChange={handleFieldChange}
									form={form}
									clients={clients}
									token={token}
									//isRecurrency={isRecurrency}
								/>
							</Col>
							<Divider 
								style={{ 
									margin: "8px 0", 
									borderColor: antToken.colorBorder,
									borderWidth: 2
								}} 
							/>
							<Col span={24}>
								<Row gutter={[10, 0]}>
									<Col span={20}>
										<Row gutter={[10, 0]}>
											<Col span={6}>
												<Title level={5} style={{ margin: "0", padding: "0" }}>Descripción</Title>
											</Col>
											<Col span={17}>
												<Row gutter={[10, 0]}>
													<Col span={5}>
														<Title level={5} style={{ margin: "0", padding: "0" }}>Tipo</Title>
													</Col>
													<Col span={5}>
														<Title level={5} style={{ margin: "0", padding: "0" }}>Cantidad</Title>
													</Col>
													<Col span={8}>
														<Title level={5} style={{ margin: "0", padding: "0" }}>Precio Unitario</Title>
													</Col>
													<Col span={6}>
														<Title level={5} style={{ margin: "0", padding: "0" }}>Descuento</Title>
													</Col>
												</Row>
											</Col>
										</Row>
									</Col>
									<Col span={4}>
										<Title level={5} style={{ margin: "0", padding: "0" }}>Total</Title>
									</Col>
								</Row>
								<Form.List name="items">
									{(fields, { add, remove }) => (
										<div style={{ display: "flex", rowGap: 0, flexDirection: "column" }}>
											{fields.map((field, index) => (
												<InvoiceLine 
													key={index}
													field={field}
													company={company}
													handleFieldChange={handleFieldChange}
													form={form} 
													changes={changes}
													remove={remove}
													exchangeRate={generalAppData.exchangeRates.gtq_usd.general}
												/>
											))}
											<Button 
												type="dashed" 
												onClick={() => add()}
												block
											>
												<PlusOutlined />
											</Button>
										</div>
									)}
								</Form.List>
								<Row gutter={[10,0]}>
									<Col span={4} >
										<Divider>Tipo de Cambio</Divider>
										<Card
											size="small"
										>
											<div style={{ display: 'flex', alignItems: 'center' }}>
												<Form.Item
													name='xchange_rate'
													style={{margin:0}}
												> 
														<InputNumber
															defaultValue={clientExchangeRate ?? 0.00}
															value={typeof clientExchangeRate === 'string' && clientExchangeRate === 'day' ? generalAppData.exchangeRates.gtq_usd.general : clientExchangeRate ?? 0.00}
															onChange={(value) => handleChangeClientExchangeRate(value)}
															style={{ width: "100%" }}
															precision={2}
															controls={false}
															disabled={typeof clientExchangeRate === 'string' && clientExchangeRate === 'day'}
														/>
												</Form.Item>
												<Tooltip title="Usar tipo de cambio del día">
													<Checkbox
														checked={typeof clientExchangeRate === 'string' && clientExchangeRate === 'day'}
														onChange={(e) => {
															if (e.target.checked) {
																handleChangeClientExchangeRate('day');
															} else {
																handleChangeClientExchangeRate(generalAppData.exchangeRates.gtq_usd.general);
															}
														}}
														style={{ marginLeft: 8 }}
													/>
												</Tooltip>
											</div>
										</Card>
									</Col>
									<Col span={10}>
										<Divider>Frases</Divider>
										<Card
											size="small"
										>
											{
												!isRecurrency &&
												frases.map((frase, index) => (<p key={index}>{satFrases[frase.type][frase.scenario]}</p>))
											}
											{
												isRecurrency &&
												originalFrases.map((frase, index) => (
													<Tooltip key={index} title={
														frases.some(original => original.type === frase.type && original.scenario === frase.scenario) ? "" : 
															<>Al guardar la recurrencia, estas frases cambiarán a: {frases.map((frase, index) => (<p key={index}>{satFrases[frase.type][frase.scenario]}</p>))}</>
													} >
														<p key={index} style={{ color: frases.some(original => original.type === frase.type && original.scenario === frase.scenario) ? "inherit" : "red" }}>
															{satFrases[frase.type][frase.scenario]}
														</p>
													</Tooltip>
												))
											}
											<Button 
												type="dashed" 
												onClick={handleOpenFrasesModal}
												block
											>
												<PlusOutlined />
											</Button>
										</Card>
										<Modal
											open={openFrasesModal}
											onCancel={handleCloseFrasesModal}
											onOk={handleCloseFrasesModal}
										>
											<div>
												{renderSelects(obligatorySelectFrases, "Obligatorios")}
												{renderSelects(optionalSelectFrases, "Opcionales")}
												<div>
													<h2>Frases Seleccionadas</h2>
													<ul>
														{frases.map((item, index) => (
															<li key={index}>
																{satFrases[item.type][item.scenario]}
															</li>
														))}
													</ul>
												</div>
											</div>
										</Modal>
									</Col>
									<Col span={10}>
										<Divider>Resumen</Divider>
										<Card>
											<div style={{ fontFamily: "monospace", lineHeight: "1.1", textAlign: "right" }}>
												<div dangerouslySetInnerHTML={{ __html: calculateInvoiceTotal(
													form.getFieldValue("items"), 
													form.getFieldValue("currency"), 
													isRecurrency? 
														clientExchangeRate : 
														clientExchangeRate == "day"? 
															generalAppData.exchangeRates.gtq_usd.general : 
															clientExchangeRate,
													antToken.colorPrimary,
													form.getFieldValue("type"),
													isRecurrency
												) }} />
											</div>
										</Card>
									</Col>
								</Row>
								<Button
									type="primary"
									size="large"
									style={{
										width: "100%",
										height: "80px",
										fontSize: "40px",
										fontWeight: "bold",
										marginTop:"10px"
									}}
									onClick={onSaveInvoice}
									disabled={loading}
								>
									{(isRecurrency || isCreate)? "Guardar" :"Emitir"}
								</Button>
								<EmailConfig
									open={openEmailsModal}
									setOpen={setOpenEmailsModal}
									invoice={invoice}
									exchangeRate={clientExchangeRate}
									clients = {clients}
									company={company}
									emailData={emailData}
									onSaveData={(value) => {
										setInvoice(value);
										onSaveChanges && onSaveChanges(value);
									}}
									isRecurrency={isRecurrency}
									isCreate={isCreate}
								/>
								<PdfReportReview
									open={openReviewInvoice}
									content={formatStructureForNotification(token, company, invoice, true)}
									extras={{
										instructions: company?.invoice_emition_specifications?.instructions ?? null,
										xchange_rate: clientExchangeRate == 'day'?
														isRecurrency?
															"(Tipo de cambio del día de emisión de factura)" :
															generalAppData.exchangeRates.gtq_usd.general :
														formatCurrency(generalAppData.exchangeRates.gtq_usd.general) === formatCurrency(clientExchangeRate) ? 
															null : 
															clientExchangeRate,
									}}
									renderContent={(invoice, extras) => <InvoiceEmitTemplate 
																			invoice={invoice} 
																			extras={extras}
																			visuals={company.invoice_emition_specifications.visuals}
																			company={company}
																			/>}
									onSetOpen={setOpenReviewInvoice}
									onConfirmAction={activeEmailModal}
									title={"Prevista de la Factura a Emitir"}
									confirmText={"Continuar"}
								/>
							</Col>
						</Row>
					</Form>
				</>
			}
			<WaitingForACompany company_id={company.id}/>
		</>
	);
};

FillInvoiceData.propTypes = {
	company: PropTypes.object.isRequired,
	invoice: PropTypes.object.isRequired,
	setInvoice: PropTypes.func.isRequired,
	generalAppData: PropTypes.object.isRequired,
	clients: PropTypes.array,
	token: PropTypes.string.isRequired,
	recurrenceRules: PropTypes.object,
	emailData: PropTypes.object,
	onSaveChanges: PropTypes.func,
	loading: PropTypes.bool,
	isCreate: PropTypes.bool,
};
